import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Paper,
  TableContainer,
  IconButton,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import AnalyticsTracker from "../../../GASetup/AnalyticsTracker";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddService from "./AddService";
import EditService from "./EditService";
import LoadingAnimation from "../../../Animations/LoadingAnimation";
import {
  cardOutline,
  fonts,
  headingFonts,
  noOutline,
} from "../../../../Styles/Styles";

export default function ServicesManagement() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingServiceId, setEditingServiceId] = useState(null); // Store the serviceId for editing

  useEffect(() => {
    // Fetch services from the backend using fetch
    const fetchServices = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/services/allservices`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        setServices(data);
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const handleEditClick = (serviceId) => {
    setEditingServiceId((prev) => (prev === serviceId ? null : serviceId));
  };

  const handleDeleteClick = async (serviceId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this service? This action cannot be undone."
    );

    if (!confirmDelete) {
      return; // Exit the function if the user cancels the action
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/services/delete/${serviceId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Error deleting service with id: ${serviceId}`);
      }

      // Remove the deleted service from the list
      setServices(services.filter((service) => service._id !== serviceId));
    } catch (error) {
      console.error("Error deleting service:", error);
    }
  };

  if (loading) {
    return <LoadingAnimation />;
  }

  return (
    <Box sx={{ padding: 3, mb: 5 }}>
      <Helmet>
        <title>Services Management - Listed.ky</title>
        <meta name="description" content="Services Management - Listed.ky" />
      </Helmet>
      <AnalyticsTracker />

      <Typography variant="h4" gutterBottom sx={{ ...headingFonts }}>
        Services Management
      </Typography>
      <TableContainer sx={{ ...cardOutline }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headingFonts }}>Image</TableCell>
              <TableCell sx={{ ...headingFonts }}>Services</TableCell>
              <TableCell sx={{ ...headingFonts }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {services
              .filter((service) => !service.parent) // Filter out main services (no parent)
              .map((mainService) => (
                <React.Fragment key={mainService._id}>
                  {/* Main Service */}
                  <TableRow>
                    <TableCell>
                      <img
                        src={mainService.image}
                        alt={mainService.name}
                        style={{ width: 100 }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...headingFonts }}>
                      {mainService.name}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleEditClick(mainService._id)}
                        sx={{
                          color: "var(--primary-color)",
                          ...noOutline,
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteClick(mainService._id)}
                        sx={{
                          color: "red",
                          ...noOutline,
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {/* Sub-Services for the Main Service */}
                  {services
                    .filter((service) => service.parent === mainService._id) // Find sub-services of the current main service
                    .map((subService) => (
                      <>
                        <TableRow key={subService._id}>
                          <TableCell sx={{ paddingLeft: 5 }}>
                            <img
                              src={subService.image}
                              alt={subService.name}
                              style={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              ...fonts,
                              paddingLeft: 5,
                            }}
                          >
                            {subService.name}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => handleEditClick(subService._id)}
                              sx={{
                                color: "var(--primary-color)",
                                ...noOutline,
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => handleDeleteClick(subService._id)}
                              sx={{
                                color: "red",
                                ...noOutline,
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        {/* Show EditService below the sub-service row */}

                        {editingServiceId === subService._id && (
                          <TableRow>
                            <TableCell colSpan={3}>
                              <EditService
                                serviceId={subService._id}
                                setEditingServiceId={setEditingServiceId}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    ))}

                  {/* Show EditService below the row if this main service or its sub-service is being edited */}
                  {editingServiceId === mainService._id && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <EditService
                          serviceId={mainService._id}
                          setEditingServiceId={setEditingServiceId}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <AddService />
    </Box>
  );
}
